import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { 
  Wallet, CreditCard, TrendingUp, TrendingDown, RefreshCw, 
  DollarSign, Users, FileText, ArrowUpRight, ArrowDownRight,
  Plus, PiggyBank, Building2, Smartphone, Banknote
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format numbers with comma as thousand separator
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0.00';
  return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Icons for different payment methods/accounts
const getAccountIcon = (name) => {
  const lowerName = name?.toLowerCase() || '';
  if (lowerName.includes('momo') || lowerName.includes('mobile')) return Smartphone;
  if (lowerName.includes('bank') || lowerName.includes('equity') || lowerName.includes('kigali')) return Building2;
  if (lowerName.includes('cash')) return Banknote;
  return PiggyBank;
};

const AccountsPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [receivables, setReceivables] = useState({ items: [], total_receivables: 0 });
  const [payables, setPayables] = useState({ items: [], total_payables: 0 });
  const [activeTab, setActiveTab] = useState('overview');
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [adjustmentForm, setAdjustmentForm] = useState({ amount: '', reason: '' });
  const [recalculating, setRecalculating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, receivablesRes, payablesRes] = await Promise.all([
        axios.get(`${API_URL}/accounts/full-summary`),
        axios.get(`${API_URL}/accounts/receivables`),
        axios.get(`${API_URL}/accounts/payables`)
      ]);
      setSummary(summaryRes.data);
      setReceivables(receivablesRes.data);
      setPayables(payablesRes.data);
    } catch (error) {
      toast.error('Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await axios.post(`${API_URL}/accounts/recalculate`);
      toast.success('Account balances recalculated');
      fetchData();
    } catch (error) {
      toast.error('Failed to recalculate balances');
    } finally {
      setRecalculating(false);
    }
  };

  const handleAdjustment = async () => {
    if (!adjustmentForm.amount || !adjustmentForm.reason) {
      toast.error('Please enter amount and reason');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/accounts/adjust/${encodeURIComponent(selectedAccount.payment_method)}?amount=${adjustmentForm.amount}&reason=${encodeURIComponent(adjustmentForm.reason)}`
      );
      toast.success('Account adjusted successfully');
      setAdjustDialogOpen(false);
      setAdjustmentForm({ amount: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust account');
    }
  };

  const openAdjustDialog = (account) => {
    setSelectedAccount(account);
    setAdjustDialogOpen(true);
  };

  const [statementDialogOpen, setStatementDialogOpen] = useState(false);
  const [statementAccount, setStatementAccount] = useState(null);
  const [statementRange, setStatementRange] = useState({ start_date: '', end_date: '' });

  const openStatementDialog = (account) => {
    setStatementAccount(account);
    setStatementRange({ start_date: '', end_date: '' });
    setStatementDialogOpen(true);
  };

  const downloadStatement = async () => {
    if (!statementAccount) return;
    try {
      toast.info('Generating statement…');
      const params = new URLSearchParams();
      if (statementRange.start_date) params.set('start_date', statementRange.start_date);
      if (statementRange.end_date) params.set('end_date', statementRange.end_date);
      const url = `${API_URL}/accounts/${encodeURIComponent(statementAccount.name)}/statement${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `statement_${statementAccount.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success('Statement downloaded');
      setStatementDialogOpen(false);
    } catch (err) {
      toast.error('Failed to generate account statement');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Wallet },
    { id: 'cash', label: 'Cash Accounts', icon: CreditCard },
    { id: 'receivables', label: 'Receivables', icon: TrendingUp },
    { id: 'payables', label: 'Payables', icon: TrendingDown }
  ];

  return (
    <div className="space-y-6" data-testid="accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Accounts Dashboard</h1>
          <p className="text-muted-foreground">Financial summary and cash position</p>
        </div>
        {isAdmin && (
          <Button 
            onClick={handleRecalculate} 
            disabled={recalculating}
            variant="outline"
            className="gap-2"
            data-testid="recalculate-btn"
          >
            <RefreshCw className={`w-4 h-4 ${recalculating ? 'animate-spin' : ''}`} />
            Recalculate Balances
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-border" data-testid="total-cash-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Wallet className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">Total Cash</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            RWF {formatNumber(summary?.total_cash || 0)}
          </p>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-border" data-testid="receivables-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ArrowUpRight className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">Accounts Receivable</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            RWF {formatNumber(summary?.total_receivables || 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {receivables.count || 0} outstanding order(s)
          </p>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-border" data-testid="payables-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <ArrowDownRight className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">Accounts Payable</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            RWF {formatNumber(summary?.total_payables || 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {payables.count || 0} unpaid item(s)
          </p>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-border" data-testid="net-position-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-violet-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-violet-600" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">Net Position</span>
          </div>
          <p className={`text-2xl font-bold ${(summary?.net_position || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            RWF {formatNumber(summary?.net_position || 0)}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-2 overflow-x-auto pb-px">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border border-border">
        {activeTab === 'overview' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Financial Overview</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Cash Accounts Summary</h4>
                <div className="space-y-3">
                  {summary?.cash_accounts?.map(account => {
                    const Icon = getAccountIcon(account.name);
                    return (
                      <div key={account.id || account.name} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5 text-muted-foreground" />
                          <span className="font-medium">{account.name}</span>
                        </div>
                        <span className={`font-semibold ${account.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          RWF {formatNumber(account.balance)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Quick Stats</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-muted-foreground">Outstanding Orders</span>
                    <span className="font-semibold">{receivables.count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-muted-foreground">Unpaid Bills</span>
                    <span className="font-semibold">{payables.count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-muted-foreground">Cash Accounts</span>
                    <span className="font-semibold">{summary?.cash_accounts?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cash' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Cash & Bank Accounts</h3>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="cash-accounts-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Account Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Payment Method</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Balance (RWF)</th>
                    {isAdmin && <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {summary?.cash_accounts?.map(account => {
                    const Icon = getAccountIcon(account.name);
                    return (
                      <tr key={account.id || account.name} className="border-b border-border hover:bg-muted/30">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-muted-foreground" />
                            <span className="font-medium">{account.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">{account.payment_method || '-'}</td>
                        <td className={`py-3 px-4 text-right font-semibold ${account.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatNumber(account.balance)}
                        </td>
                        {isAdmin && (
                          <td className="py-3 px-4 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openStatementDialog(account)}
                              data-testid={`statement-${account.name}`}
                              title="Generate statement"
                            >
                              Statement
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => openAdjustDialog(account)}
                              data-testid={`adjust-${account.name}`}
                            >
                              Adjust
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/30 font-semibold">
                    <td colSpan={2} className="py-3 px-4">Total</td>
                    <td className={`py-3 px-4 text-right ${(summary?.total_cash || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      RWF {formatNumber(summary?.total_cash || 0)}
                    </td>
                    {isAdmin && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'receivables' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Accounts Receivable (Outstanding Orders)</h3>
            {receivables.items?.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No outstanding receivables</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="receivables-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Order ID</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Order Total</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Amount Paid</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Balance Due</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivables.items?.map(item => (
                      <tr key={item.id} className="border-b border-border hover:bg-muted/30">
                        <td className="py-3 px-4 font-medium">{item.customer_name || '-'}</td>
                        <td className="py-3 px-4 text-muted-foreground font-mono text-sm">{item.id?.slice(0, 8)}...</td>
                        <td className="py-3 px-4 text-right">{formatNumber(item.total)}</td>
                        <td className="py-3 px-4 text-right text-emerald-600">{formatNumber(item.amount_paid || 0)}</td>
                        <td className="py-3 px-4 text-right font-semibold text-amber-600">{formatNumber(item.balance_due)}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {item.status || 'unpaid'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/30 font-semibold">
                      <td colSpan={4} className="py-3 px-4">Total Receivables</td>
                      <td className="py-3 px-4 text-right text-amber-600">RWF {formatNumber(receivables.total_receivables)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'payables' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Accounts Payable (Outstanding Bills)</h3>
            {payables.items?.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No outstanding payables</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="payables-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Reference</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Description</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Amount Due</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payables.items?.map((item, index) => (
                      <tr key={item.id || index} className="border-b border-border hover:bg-muted/30">
                        <td className="py-3 px-4 font-mono text-sm">{item.reference_id?.slice(0, 8) || '-'}...</td>
                        <td className="py-3 px-4">
                          <span className="capitalize">{item.reference_type || 'other'}</span>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">{item.description || '-'}</td>
                        <td className="py-3 px-4 text-right font-semibold text-amber-600">{formatNumber(item.amount)}</td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/30 font-semibold">
                      <td colSpan={3} className="py-3 px-4">Total Payables</td>
                      <td className="py-3 px-4 text-right text-amber-600">RWF {formatNumber(payables.total_payables)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Adjust Account Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Account Balance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium">Account</label>
              <p className="text-lg font-semibold text-primary">{selectedAccount?.name}</p>
              <p className="text-sm text-muted-foreground">
                Current Balance: RWF {formatNumber(selectedAccount?.balance || 0)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Adjustment Amount (RWF)</label>
              <Input
                type="number"
                placeholder="Enter positive or negative amount"
                value={adjustmentForm.amount}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })}
                data-testid="adjustment-amount"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use negative values to decrease, positive to increase
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Reason</label>
              <Input
                placeholder="e.g., Physical cash count adjustment"
                value={adjustmentForm.reason}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                data-testid="adjustment-reason"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAdjustment} data-testid="confirm-adjustment-btn">
                Apply Adjustment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Account statement dialog */}
      <Dialog open={statementDialogOpen} onOpenChange={setStatementDialogOpen}>
        <DialogContent className="max-w-md" data-testid="account-statement-dialog">
          <DialogHeader>
            <DialogTitle>Account statement</DialogTitle>
            <DialogDescription>Generate a PDF showing all cash in/out for this account with running balance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Account: <strong className="text-foreground">{statementAccount?.name}</strong>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From (optional)</Label>
                <Input type="date" value={statementRange.start_date} onChange={(e) => setStatementRange({ ...statementRange, start_date: e.target.value })} data-testid="account-stmt-start" />
              </div>
              <div>
                <Label className="text-xs">To (optional)</Label>
                <Input type="date" value={statementRange.end_date} onChange={(e) => setStatementRange({ ...statementRange, end_date: e.target.value })} data-testid="account-stmt-end" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Leave both dates empty for an all-time statement.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setStatementDialogOpen(false)}>Cancel</Button>
              <Button onClick={downloadStatement} data-testid="account-stmt-download">Download PDF</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsPage;
